import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

interface SiteSummary {
  siteId: number;
  cliCalls: number;
  mcpCalls: number;
  copilotCalls: number;
  lastSeen: string;
  cliUsers: number;
  cliVersions: string[];
}

interface TopAction {
  source: string;
  action: string;
  count: number;
}

interface ReportSummary {
  periodDays: number;
  totalCalls: number;
  cliCalls: number;
  mcpCalls: number;
  copilotCalls: number;
  activeSites: number;
  cliSites: number;
  mcpSites: number;
}

interface ReportData {
  summary: ReportSummary;
  bySite: SiteSummary[];
  topActions: TopAction[];
}

const SOURCE_COLORS: Record<string, string> = {
  cli: "bg-blue-100 text-blue-700",
  mcp: "bg-green-100 text-green-700",
  copilot: "bg-purple-100 text-purple-700",
};

export default function ToolUsageReport() {
  const api = useApi();
  const [data, setData] = useState<ReportData | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    api.messaging
      .get(`ToolUsage/report?days=${days}`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const filteredSites = (data?.bySite ?? []).filter(
    (s) =>
      !search ||
      String(s.siteId).includes(search) ||
      s.cliVersions?.some((v) => v?.includes(search))
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tool Usage Report</h1>
          <p className="text-sm text-gray-500 mt-1">
            CLI commands · MCP tool calls · Copilot queries — last {days} days
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="border rounded px-3 py-1.5 text-sm"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {loading && <div className="text-gray-400 text-sm">Loading...</div>}

      {!loading && data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Total Calls", value: data.summary.totalCalls },
              { label: "CLI Calls", value: data.summary.cliCalls, sub: `${data.summary.cliSites} sites` },
              { label: "MCP Calls", value: data.summary.mcpCalls, sub: `${data.summary.mcpSites} sites` },
              { label: "Active Sites", value: data.summary.activeSites },
            ].map((card) => (
              <div key={card.label} className="bg-white border rounded-lg p-4">
                <div className="text-2xl font-bold text-gray-900">
                  {card.value.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">{card.label}</div>
                {card.sub && <div className="text-xs text-gray-400 mt-1">{card.sub}</div>}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Top Actions */}
            <div className="bg-white border rounded-lg p-4">
              <h2 className="font-semibold text-gray-700 mb-3">Top Actions</h2>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {data.topActions.slice(0, 20).map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          SOURCE_COLORS[a.source] ?? "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {a.source}
                      </span>
                      <span className="font-mono text-gray-700">{a.action}</span>
                    </div>
                    <span className="text-gray-500 font-medium">{a.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CLI Versions in use */}
            <div className="bg-white border rounded-lg p-4">
              <h2 className="font-semibold text-gray-700 mb-3">CLI Versions in Use</h2>
              <div className="space-y-1 text-sm">
                {Array.from(
                  new Set(data.bySite.flatMap((s) => s.cliVersions ?? []))
                )
                  .filter(Boolean)
                  .map((v) => {
                    const count = data.bySite.filter((s) =>
                      s.cliVersions?.includes(v)
                    ).length;
                    return (
                      <div key={v} className="flex justify-between">
                        <span className="font-mono text-blue-700">v{v}</span>
                        <span className="text-gray-500">{count} site{count !== 1 ? "s" : ""}</span>
                      </div>
                    );
                  })}
                {data.bySite.every((s) => !s.cliVersions?.length) && (
                  <div className="text-gray-400">No CLI usage yet</div>
                )}
              </div>
            </div>
          </div>

          {/* Per-site breakdown */}
          <div className="bg-white border rounded-lg">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-semibold text-gray-700">Per-Site Breakdown</h2>
              <input
                type="text"
                placeholder="Filter by site ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border rounded px-3 py-1 text-sm w-48"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b bg-gray-50">
                    <th className="px-4 py-2">Site ID</th>
                    <th className="px-4 py-2">CLI Calls</th>
                    <th className="px-4 py-2">MCP Calls</th>
                    <th className="px-4 py-2">CLI Users</th>
                    <th className="px-4 py-2">CLI Version(s)</th>
                    <th className="px-4 py-2">Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSites.map((s) => (
                    <tr key={s.siteId} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono font-medium">{s.siteId}</td>
                      <td className="px-4 py-2">
                        {s.cliCalls > 0 ? (
                          <span className="text-blue-700 font-medium">{s.cliCalls}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {s.mcpCalls > 0 ? (
                          <span className="text-green-700 font-medium">{s.mcpCalls}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-600">{s.cliUsers || "—"}</td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-500">
                        {s.cliVersions?.filter(Boolean).join(", ") || "—"}
                      </td>
                      <td className="px-4 py-2 text-gray-400">
                        {new Date(s.lastSeen).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                  {filteredSites.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                        No data for this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && !data && (
        <div className="text-gray-400 text-sm">Failed to load report.</div>
      )}
    </div>
  );
}
